import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ErrorState } from "@/components/shared/ErrorState";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { formatDateTime } from "@/lib/formatters";
import { TablePagination } from "../../CustomerGroups/component/TablePagination";
import { filterHistory } from "./physicalClientTableSearch";
import { describeHistoryChange, HISTORY_ACTION_LABELS } from "./physicalClientLabels";
import { TableSearchField } from "./TableSearchField";
import { useLocalTable } from "./useLocalTable";
import { usePhysicalClientHistory } from "./usePhysicalClientHistory";

interface PhysicalClientHistoryTableProps {
  physicalClientId: number;
}

/**
 * Histórico do cliente (cadastro, edições, situação com motivo e endereços), mais recente primeiro, com busca e
 * paginação na tela: a API devolve o histórico inteiro do cliente.
 */
export function PhysicalClientHistoryTable({ physicalClientId }: PhysicalClientHistoryTableProps) {
  const query = usePhysicalClientHistory(physicalClientId);
  const entries = useMemo(() => query.data ?? [], [query.data]);
  const table = useLocalTable(entries, filterHistory);

  if (query.isLoading) return <Skeleton className="h-32 w-full" aria-busy="true" />;
  if (query.isError)
    return <ErrorState message="Não foi possível carregar o histórico." onRetry={() => void query.refetch()} />;
  if (!entries.length) return <EmptyState title="Sem histórico" message="Nenhuma alteração registrada." className="py-6" />;

  return (
    <div className="space-y-4">
      <TableSearchField
        label="Buscar no histórico"
        placeholder="Ação, detalhe, motivo, quem ou data"
        value={table.search}
        onChange={table.setSearch}
      />

      {table.filtered.length === 0 ? (
        <EmptyState title="Nenhum registro encontrado" message="Revise o texto da busca." className="py-6" />
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>Detalhe</TableHead>
                  <TableHead>Motivo / observação</TableHead>
                  <TableHead>Quem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {table.pageItems.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap">{formatDateTime(new Date(entry.performedAt))}</TableCell>
                    <TableCell>{HISTORY_ACTION_LABELS[entry.action] ?? entry.action}</TableCell>
                    <TableCell>{describeHistoryChange(entry)}</TableCell>
                    <TableCell>{entry.comment || "—"}</TableCell>
                    <TableCell>{entry.performedBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            page={table.page}
            pageSize={table.pageSize}
            total={table.filtered.length}
            unfilteredTotal={entries.length}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        </>
      )}
    </div>
  );
}
